import React, { useCallback, useEffect, useRef, useState } from 'react';
import useBackgroundPoll from '../helpers/useBackgroundPoll';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { Badge, Button, Drawer, Empty, List, Space, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import notificationService from 'services/notification';
import { addMenu } from 'redux/slices/menu';
import dayjs from 'dayjs';
import { formatDisplayDateTime } from '../helpers/displayDate';

/**
 * Admin bell inbox — talks to /dashboard/admin/notifications endpoints
 * (list, unread-count, mark-read, mark-all-read). Polls the count
 * every 20 seconds, plays a short chime and (with permission) fires a
 * browser system notification whenever the unread total goes up. The
 * chime is a WebAudio beep synthesised at click time — no asset to
 * ship — with a `localStorage.ks_admin_muted = '1'` off-switch the
 * user can flip from the dropdown.
 *
 * The counter is also mirrored to the `window` so sidebar items can
 * subscribe (see `sidebar.js` / `useAdminNotificationCounts`).
 */
export default function NotificationBar() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [, setPerType] = useState({});
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem('ks_admin_muted') === '1'; }
    catch { return false; }
  });
  const prevUnreadRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const dispatchCounts = useCallback((total, breakdown) => {
    try {
      window.__ksAdminNotificationCounts = { total, perType: breakdown };
      window.dispatchEvent(new CustomEvent('ks:admin-notification-counts', {
        detail: { total, perType: breakdown },
      }));
    } catch { /* no-op */ }
  }, []);

  const playChime = useCallback(() => {
    if (muted) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.42);
      osc.onended = () => ctx.close?.();
    } catch { /* silent */ }
  }, [muted]);

  const showBrowserNotification = useCallback((row) => {
    if (muted) return;
    try {
      if (typeof Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return;
      const n = new Notification(row.title || 'Kamil Store', {
        body: row.body || '',
        tag: `ks-admin-${row._id || row.id}`,
        icon: '/img/logo.png',
      });
      n.onclick = () => {
        window.focus();
        if (row.href) navigate(row.href);
        n.close();
      };
    } catch { /* silent */ }
  }, [muted, navigate]);

  const requestBrowserPermission = useCallback(() => {
    try {
      if (typeof Notification === 'undefined') return;
      if (Notification.permission === 'default') Notification.requestPermission();
    } catch { /* silent */ }
  }, []);

  const fetchCount = useCallback(async () => {
    try {
      const res = await notificationService.bellUnreadCount();
      const data = res?.data?.data ?? res?.data ?? {};
      const total = data.total ?? 0;
      const breakdown = data.perType ?? {};
      if (!mountedRef.current) return;
      if (total > prevUnreadRef.current && prevUnreadRef.current !== 0) {
        // A new one arrived while we were open enough to keep polling —
        // ping the user and offer the browser system notification.
        playChime();
        try {
          const listRes = await notificationService.bellList({ page: 1, perPage: 1 });
          const latest = (listRes?.data?.data?.items ?? [])[0];
          if (latest) showBrowserNotification(latest);
        } catch { /* silent */ }
      }
      prevUnreadRef.current = total;
      setUnread(total);
      setPerType(breakdown);
      dispatchCounts(total, breakdown);
    } catch { /* silent */ }
  }, [playChime, showBrowserNotification, dispatchCounts]);

  /*
   * Slower in a background tab, never stopped: the bell exists to announce a
   * new order to somebody working in another window.
   */
  useBackgroundPoll(fetchCount, { visibleMs: 20000, hiddenMs: 80000 });

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationService.bellList({ page: 1, perPage: 30 });
      const data = res?.data?.data ?? res?.data ?? {};
      setItems(data.items ?? []);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const handleOpen = () => {
    requestBrowserPermission();
    loadList();
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleRow = async (row) => {
    if (!row?.isRead) {
      await notificationService.bellMarkRead(row._id ?? row.id).catch(() => {});
    }
    if (row?.href) {
      dispatch(addMenu({
        url: row.href.replace(/^\/+/, ''),
        id: `admin-notification-${row._id ?? row.id}`,
        name: row.title || t('notification'),
      }));
      navigate(row.href);
    }
    setOpen(false);
    fetchCount();
    loadList();
  };

  const handleMarkAll = async () => {
    await notificationService.bellMarkAllRead().catch(() => {});
    fetchCount();
    loadList();
  };

  const toggleMuted = () => {
    setMuted((v) => {
      const next = !v;
      try { localStorage.setItem('ks_admin_muted', next ? '1' : '0'); }
      catch { /* ignore */ }
      return next;
    });
  };

  return (
    <>
      <span className='icon-button' onClick={handleOpen}>
        <Badge count={unread}>
          <BellOutlined style={{ fontSize: 20 }} />
        </Badge>
      </span>

      <Drawer
        title={
          <Space>
            <span>{t('notifications')}</span>
            {unread > 0 ? <Tag color='blue'>{unread}</Tag> : null}
          </Space>
        }
        placement='right'
        onClose={handleClose}
        visible={open}
        width={420}
        extra={
          <Space>
            <Tooltip title={muted ? t('unmute') : t('mute')}>
              <Button size='small' onClick={toggleMuted}>
                {muted ? '🔕' : '🔔'}
              </Button>
            </Tooltip>
            <Button size='small' icon={<CheckOutlined />} onClick={handleMarkAll}>
              {t('read.all')}
            </Button>
          </Space>
        }
      >
        {items.length === 0 && !loading ? (
          <Empty description={t('no.data')} />
        ) : (
          <List
            size='small'
            loading={loading}
            dataSource={items}
            renderItem={(row) => (
              <List.Item
                className='list-clickable'
                onClick={() => handleRow(row)}
                style={{ cursor: 'pointer', paddingLeft: 12, paddingRight: 12 }}
              >
                <div style={{ width: '100%', display: 'flex', gap: 10 }}>
                  <span style={{
                    display: 'block',
                    width: 8, height: 8, borderRadius: '50%',
                    background: row.isRead ? 'transparent' : '#ea580c',
                    marginTop: 8, flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{row.title}</div>
                    {row.body ? (
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {row.body}
                      </div>
                    ) : null}
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                      {dayjs(row.createdAt).fromNow?.() ?? formatDisplayDateTime(row.createdAt)}
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </Drawer>
    </>
  );
}
